import React from 'react';
import { useIntl } from 'react-intl';
import { Row, Col, Card, Typography, Space, theme, Divider } from 'antd';
import {
  EnvironmentOutlined,
  PhoneOutlined,
  MailOutlined,
  GlobalOutlined,
  FacebookFilled,
  InstagramFilled
} from '@ant-design/icons';
import HotelProfileStateHook from '@/hooks/useHotelProfileState';
import { HtmlText } from '@/components/HtmlText';
import { normalizeExternalUrl, normalizeSocialUrl } from '@/utils/contactLinkUtils';

const TikTokIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: '1em', height: '1em' }}>
    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
  </svg>
);

// Custom LINE Icon Component
const LineIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: '1em', height: '1em' }}>
    <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63h2.386c.346 0 .627.285.627.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63.346 0 .628.285.628.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.282.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314" />
  </svg>
);

const FaxIcon = () => (
  <svg viewBox="0 0 512 512" fill="currentColor" style={{ width: '1em', height: '1em' }}>
    <path d="M160 64l0 80 64 0 0-80 146.7 0 45.3 45.3 0 34.7 64 0 0-34.7c0-17-6.7-33.3-18.7-45.3L416 18.7C404 6.7 387.7 0 370.7 0L224 0c-35.3 0-64 28.7-64 64zM32 128c-17.7 0-32 14.3-32 32L0 448c0 17.7 14.3 32 32 32l48 0c17.7 0 32-14.3 32-32l0-288c0-17.7-14.3-32-32-32l-48 0zm448 64l-320 0 0 256c0 17.7 14.3 32 32 32l288 0c17.7 0 32-14.3 32-32l0-224c0-17.7-14.3-32-32-32zM224 288a24 24 0 1 1 48 0 24 24 0 1 1 -48 0zm0 96a24 24 0 1 1 48 0 24 24 0 1 1 -48 0zM336 264a24 24 0 1 1 0 48 24 24 0 1 1 0-48zM312 384a24 24 0 1 1 48 0 24 24 0 1 1 -48 0zM424 264a24 24 0 1 1 0 48 24 24 0 1 1 0-48zM400 384a24 24 0 1 1 48 0 24 24 0 1 1 -48 0z" />
  </svg>
);

const { Title, Text, Link } = Typography;

export default function HotelContactInfo() {
  const { token } = theme.useToken();
  const intl = useIntl();

  const { dataSource } = HotelProfileStateHook()
  const { hotelIntro, websiteUrl, address, emails, phones, fax, socialMedia } = dataSource || {}

  const hotelData = {
    name: hotelIntro?.title || null,
    address: address || "",
    phones: phones || [],
    emails: emails || [],
    fax: fax || "",
    websites: websiteUrl ? [websiteUrl] : [],
    social: socialMedia ? {
      facebook: socialMedia?.facebook || null,
      instagram: socialMedia?.instagram || null,
      line: socialMedia?.line || null,
      tiktok: socialMedia?.tiktok || null,
    } : null
  };

  const contactSections = [
    {
      icon: <EnvironmentOutlined />,
      label: intl.formatMessage({ id: 'hotel.address', defaultMessage: 'Address' }),
      items: [`${hotelData.address}`],
      type: 'text',
      visible: !!hotelData.address
    },
    {
      icon: <PhoneOutlined />,
      label: intl.formatMessage({ id: 'hotel.phone', defaultMessage: 'Phone' }),
      items: hotelData.phones,
      type: 'tel',
      visible: hotelData.phones.length > 0
    },
    {
      icon: <FaxIcon />,
      label: intl.formatMessage({ id: 'hotel.fax', defaultMessage: 'Fax' }),
      items: hotelData.fax ? [hotelData.fax] : [],
      type: 'tel',
      visible: !!hotelData.fax
    },
    {
      icon: <MailOutlined />,
      label: intl.formatMessage({ id: 'hotel.email', defaultMessage: 'Email' }),
      items: hotelData.emails,
      type: 'email',
      visible: hotelData.emails.length > 0
    },
    {
      icon: <GlobalOutlined />,
      label: intl.formatMessage({ id: 'hotel.website', defaultMessage: 'Website' }),
      items: hotelData.websites,
      type: 'url',
      visible: hotelData.websites.length > 0
    }
  ].filter(item => item.visible);

  const socialButtons = [
    {
      icon: <FacebookFilled />,
      label: hotelData.social?.facebook || null,
      href: hotelData.social?.facebook ? normalizeSocialUrl(hotelData.social.facebook, 'facebook') : '#',
      color: '#1877F2',
      visible: !!hotelData.social?.facebook
    },
    {
      icon: <InstagramFilled />,
      label: hotelData.social?.instagram || null,
      href: hotelData.social?.instagram ? normalizeSocialUrl(hotelData.social.instagram, 'instagram') : '#',
      color: '#E4405F',
      visible: !!hotelData.social?.instagram
    },
    {
      icon: <LineIcon />,
      label: hotelData.social?.line || null,
      href: hotelData.social?.line ? normalizeSocialUrl(hotelData.social.line, 'line') : '#',
      color: '#06C755',
      visible: !!hotelData.social?.line
    },
    {
      icon: <TikTokIcon />,
      label: hotelData.social?.tiktok || null,
      href: hotelData.social?.tiktok ? normalizeSocialUrl(hotelData.social.tiktok, 'tiktok') : '#',
      color: '#010101',
      visible: !!hotelData.social?.tiktok
    }
  ].filter(item => item.visible);

  const getHref = (item: string, type: string) => {
    if (type === 'tel') return `tel:${item}`;
    if (type === 'email') return `mailto:${item}`;
    if (type === 'url') return normalizeExternalUrl(item);
    return 'https://maps.google.com';
  };

  if(contactSections.length === 0 && socialButtons.length === 0) {
    return null; // ไม่แสดงคอมโพเนนต์ถ้าไม่มีข้อมูลติดต่อหรือโซเชียลมีเดีย
  }

  return (
    <Card
      style={{
        borderRadius: token.borderRadiusLG,
        border: 'none',
        boxShadow: '0 2px 12px rgba(0,0,0,0.04)'
      }}
    >
      <Row gutter={[48, 48]}>
        {/* Contact Info Section - Minimal List Style */}
        <Col xs={24} md={12}>
          <Space orientation="vertical" size="large" style={{ width: '100%' }}>
            <Title level={3} style={{ marginBottom: 0 }}>{intl.formatMessage({ id: 'hotel.contactInfo', defaultMessage: 'Contact Information' })}</Title>

            <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
              {contactSections.map((section, index) => (
                <div key={index}>
                  <Space
                    align="start"
                    size="middle"
                    style={{
                      width: '100%',
                      padding: `${token.paddingSM}px 0`
                    }}
                  >
                    {/* Icon */}
                    <div style={{
                      fontSize: 20,
                      color: token.colorPrimary,
                      marginTop: 2,
                      minWidth: 20
                    }}>
                      {section.icon}
                    </div>

                    {/* Content */}
                    <div style={{ flex: 1 }}>
                      <Text
                        strong
                        style={{
                          display: 'block',
                          marginBottom: 4,
                          color: token.colorTextSecondary,
                          fontSize: token.fontSizeSM
                        }}
                      >
                        {section.label}
                      </Text>

                      <Space orientation="vertical" size={4} style={{ width: '100%' }}>
                        {section.items.map((item, itemIndex) => (
                          <div key={itemIndex}>
                            {section.type === 'text' ? (
                              <Text style={{ color: token.colorText }}>
                                <HtmlText value={item} />
                              </Text> 
                            ) : (
                              <Link
                                href={getHref(item, section.type)}
                                target={section.type === 'url' ? '_blank' : undefined}
                                style={{
                                  color: token.colorText,
                                  textDecoration: 'none',
                                  transition: 'color 0.3s'
                                }}
                                onMouseEnter={(e) => {
                                  (e.target as HTMLElement).style.color = token.colorPrimary;
                                }}
                                onMouseLeave={(e) => {
                                  (e.target as HTMLElement).style.color = token.colorText;
                                }}
                              >
                                {item}
                              </Link>
                            )}
                          </div>
                        ))}
                      </Space>
                    </div>
                  </Space>

                  {/* Divider ยกเว้นรายการสุดท้าย */}
                  {index < contactSections.length - 1 && (
                    <Divider style={{ margin: '12px 0' }} />
                  )}
                </div>
              ))}
            </Space>
          </Space>
        </Col>

        {/* Social Media Section - Minimal Buttons */}
        <Col xs={24} md={12}>
          <Space orientation="vertical" size="large" style={{ width: '100%' }}>
            <Title level={3} style={{ marginBottom: 0 }}>{intl.formatMessage({ id: 'hotel.followUs', defaultMessage: 'Follow Us' })}</Title>

            <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
              {socialButtons.map((social, index) => (
                <Link
                  key={index}
                  href={social.href}
                  target="_blank"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: `${token.paddingSM}px ${token.padding}px`,
                    backgroundColor: token.colorBgContainer,
                    border: `1px solid ${token.colorBorderSecondary}`,
                    borderRadius: token.borderRadius,
                    textDecoration: 'none',
                    transition: 'all 0.3s',
                    width: '100%'
                  }}
                  onMouseEnter={(e) => {
                    const target = e.currentTarget as HTMLElement;
                    target.style.borderColor = social.color;
                    target.style.backgroundColor = `${social.color}08`;
                  }}
                  onMouseLeave={(e) => {
                    const target = e.currentTarget as HTMLElement;
                    target.style.borderColor = token.colorBorderSecondary;
                    target.style.backgroundColor = token.colorBgContainer;
                  }}
                >
                  <div style={{
                    fontSize: 24,
                    color: social.color,
                    marginRight: 12,
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    {social.icon}
                  </div>
                  <Text strong style={{ color: token.colorText }}>
                    {social.label}
                  </Text>
                </Link>
              ))}
            </Space>
          </Space>
        </Col>
      </Row>
    </Card>
  );
}